import React, { useState } from 'react'
import LeftWindowIcon from 'packages/@slerp/assets/integrations/left-wing.png'
import RightWindowIcon from 'packages/@slerp/assets/integrations/right-wing.png'
import styled from '@emotion/styled'
import { Integration } from './types'
import IntegrationCard from './IntegrationCard'
import LightspeedIcon from 'packages/@slerp/assets/integrations/lightspeed.png'
import { useQuery } from '@apollo/client'
import { GET_MERCHANT_INTEGRATIONS } from './queries'
import { useSession } from 'packages/@slerp/accounts'
import Loading from 'components/Utils/Loading'
import {
  PartnerName,
  PARTNER_SLUGS,
  PARTNER_DISPLAY_NAMES,
  INTEGRATION_CATEGORIES,
  isPartner
} from './constants'

//hardcode for now
const INTEGRATIONS: Record<string, Integration[]> = {
  [INTEGRATION_CATEGORIES.POS]: [
    {
      active: false,
      slug: PARTNER_SLUGS.LIGHTSPEED,
      partnerName: PartnerName.LIGHTSPEED,
      name: PARTNER_DISPLAY_NAMES.LIGHTSPEED,
      description:
        'Integrate Lightspeed with Slerp to sync point-of-sale data and ensure loyalty and customer interactions work seamlessly at checkout.',
      icon: LightspeedIcon
    }
  ]
}

const Integrations = () => {
  const [currentCategory, setCurrentCategory] = useState<string | null>(null)
  const [integrations, setIntegrations] =
    useState<Record<string, Integration[]>>(INTEGRATIONS)
  const { merchant } = useSession()

  const { loading } = useQuery<{
    partners_merchants: { partner: { name: string } }[]
  }>(GET_MERCHANT_INTEGRATIONS, {
    variables: { merchantId: merchant?.id },
    onCompleted: (data) => {
      // hardcode lightspeed integration for now
      if (
        data?.partners_merchants?.some((partner) =>
          isPartner(partner.partner.name, PartnerName.LIGHTSPEED)
        )
      ) {
        const updatedIntegrations = { ...INTEGRATIONS }
        updatedIntegrations[INTEGRATION_CATEGORIES.POS][0].active = true

        setIntegrations(updatedIntegrations)
      }
    }
  })

  if (loading) {
    return <Loading />
  }

  return (
    <Root>
      <Header>
        <WingIcon src={LeftWindowIcon} />
        <TitleWrapper>
          <Title>Integrations to help you grow</Title>
          <Description>
            Our integrated partners offer services that seamlessly connect with
            Slerp, helping you run your business more efficiently across every
            touchpoint.
          </Description>
        </TitleWrapper>
        <WingIcon src={RightWindowIcon} />
      </Header>
      <Sidebar>
        <CategoryTitle>Categories</CategoryTitle>
        <CategoryList>
          <CategoryEntry
            active={currentCategory === null}
            key='all'
            onClick={() => setCurrentCategory(null)}
          >
            <span>All</span>
            <span>
              {Object.keys(integrations).reduce(
                (acc, category) => acc + integrations[category].length,
                0
              )}
            </span>
          </CategoryEntry>
          {Object.keys(INTEGRATIONS).map((category: string) => (
            <CategoryEntry
              active={currentCategory === category}
              key={category}
              onClick={() => setCurrentCategory(category)}
            >
              <span>{category}</span>
              <span>{INTEGRATIONS[category].length}</span>
            </CategoryEntry>
          ))}
        </CategoryList>
      </Sidebar>
      <Content>
        {Object.entries(integrations).map(([category, integrations]) => (
          <div key={category}>
            <CategoryTitle>
              {category}&nbsp;&nbsp;&nbsp;{integrations.length}
            </CategoryTitle>
            <IntegrationList>
              {integrations.map((integration) => (
                <IntegrationCard
                  key={integration.slug}
                  integration={integration}
                />
              ))}
            </IntegrationList>
          </div>
        ))}
      </Content>
    </Root>
  )
}

const WingIcon = styled.img`
  height: 192px;
  object-fit: contain;
  object-position: center;
`

const Root = styled.div`
  display: grid;
  grid-template: 'header header' min-content 'sidebar content' 1fr / 248px 1fr;
  height: 100%;
`

const Header = styled.div`
  grid-area: header;

  width: calc(100% + 48px);
  margin-left: -24px;
  margin-right: -24px;
  overflow: hidden;

  display: flex;
  justify-content: space-between;
`

const TitleWrapper = styled.div`
  max-width: 700px;
  padding: 55px 0;
  text-align: center;
`

const Title = styled.h2`
  max-width: 700px;

  font-size: 28px;
  font-weight: 600;
  line-height: 100%;
  color: #4a4a68;
`

const Description = styled.p`
  max-width: 700px;

  font-size: 16px;
  font-weight: 500;
  line-height: 100%;
  color: #77778e;
`

const Sidebar = styled.div`
  grid-area: sidebar;

  margin-top: 24px;
  padding: 0 24px 24px 24px;
  border-right: 1px solid #f3f2f4;
`

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  line-height: 100%;
  color: #4a4a68;
`

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const CategoryEntry = styled.li<{ active: boolean }>(
  ({ active }) => `
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 4px 8px;
  margin-bottom: 8px;
  border-radius: 4px;

  font-size: 14px;
  font-weight: 500;
  line-height: 100%;
  color: ${active ? '#0A73E8' : '#4A4A68'};

  ${
    active &&
    `
    background-color: #F6FAFF;
  `
  }
`
)

const Content = styled.div`
  grid-area: content;

  padding: 24px;
`

const IntegrationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

export default Integrations
